import decodeHTML from '@exp/utilities/decodeHTML';

import { baseThunkConfig, createAppSlice } from '@exp/redux/createAppSlice';

import { getGeocode } from '../api/address';
import { getTeamClassPageKeys } from '../api/community';
import { getTeamClasses } from '../api/team';
import {
  getApplication,
  getEmailRules,
  getLocations,
  getOnboardingAffiliations,
  getPublicApplicationForm,
  getRenewalApplicationForm,
  getTeam,
  getTeamCode,
  getTeams,
  postApplication,
  postApplicationVerificationEmail,
  postLocation,
  postTeamCode,
  postVerificationStatuses,
  putLocation,
  putUnsubmitApplication,
} from '../api/user-affiliations';
import {
  AffiliationStatus,
  ApplicationErrors,
  ApplicationStatus,
  AttachmentPreferences,
  QuestionType,
  TeamType,
} from '../constants/constants';
import { isApplicationPending, isApplicationSubmitted } from '../utilities/statuses';
import {
  formatAddress,
  formatLocationCount,
  formatMemberCount,
  getOnbCacheKey,
  hasLinkedTeams,
  isFastPassCode,
  readOnbCache,
  removeSuggestionFromStorage,
} from '../utilities/utilities';
import { selectContext } from './context';

const parseApplicationForm = (data) => {
  if (!data) return null;

  const questions = (data.questions || []).map((question) => {
    const q = { ...question };

    // Sanitize the question text
    q.text = decodeHTML(q.text);

    // Format the answers on any DATE fields to be what the input wants (YYYY-MM-DD)
    if (q.questionType === QuestionType.DATE && q.comment) {
      const date = new Date(q.comment).toISOString();
      q.comment = date.substring(0, date.indexOf('T'));
    }

    // Update the requireAnswer field on any IMAGE questions where the attachmentPreference=REQUIRED
    if (q.questionType === QuestionType.IMAGE && !q.requireAnswer) {
      q.requireAnswer = q.attachmentPreference === AttachmentPreferences.REQUIRED;
    }

    return q;
  });

  return {
    ...data,
    questions,
  };
};

const parseTeam = (data) => ({
  ...data,
  brandCount: data.brandCount || 0,
  locationCount: data.locationCount || 0,
  locationCountRange: data.type === TeamType.RETAIL ? formatLocationCount(data.locationCount || 0) : null,
  locationText: formatAddress(data.location?.address),
  memberCount: data.memberCount || 0,
  memberCountRange: formatMemberCount(data.memberCount || 0),
  imagePath: data.teamImage?.imagePath || data.teamImage?.defaultImagePath || '/user/default-team.jpg',
  overrideImagePath: data.teamImageOverride?.imagePath,
  isDefaultImage: !data.teamImage?.imagePath,
  shortName: data.shortName || data.name,
  teamClassId: data.teamClassId,
  type: data.type || data.teamType,
});

// ---------------------------------------------------------------------------
// Shared helper for submitApplication / resubmitApplication
// ---------------------------------------------------------------------------

/**
 * Builds the request body, POSTs to /applications, and shapes the return payload.
 */
async function postApplicationRequest(data, context) {
  const { applicationForm, groupCode, location, questions, team, verificationEmail } = data;

  const request = {
    applicationType: applicationForm.applicationType,
    locationId: location?.locationId,

    // Map the defined questions to the question responses; this keeps transient questions out
    questions: applicationForm.questions
      .filter((q) => questions?.[q.questionId])
      .map((q) => {
        // Limit what gets sent to the backend
        const value = questions[q.questionId];
        return {
          comment: value.comment,
          field: q.field, // TODO is necessary? BE shouldn't be relying on the FE to pass this.
          questionId: q.questionId,
          questionType: q.questionType,
          teamInfluencerClassQuestion: q.teamInfluencerClassQuestion, // TODO is necessary? BE shouldn't be relying on the FE to pass this.
          userAccountConnectionId: value.userAccountConnectionId,
          userAnswers: value.userAnswers,
        };
      }),

    // Pass the referral values if they exist
    referer: data.referer,
    referredOrgId: data.referredOrgId,

    teamApplicationFormId: applicationForm.teamApplicationFormId,
    teamCode: groupCode,
    teamId: team.teamId,
    userUuid: context.user?.uuid,

    // Send the user's primary email if they didn't supply a verification email
    verificationEmail: verificationEmail || context.user?.primaryEmailId,
  };

  let result;
  try {
    const attachments = Object.values(questions || {}).filter((q) => q.attachment);
    const attachmentMap = attachments.length
      ? Object.fromEntries(attachments.map((q) => [q.questionId, q.attachment]))
      : undefined;
    result = await postApplication(request, attachmentMap);
  } catch (e) {
    const { messages, type } = e.response.data;
    result = {
      errorStatus: type || e.response.statusText,
      errorStatusMessage: messages?.[0] || e.response.statusText,
    };
  }

  const isPending = isApplicationPending(result.applicationStatus);
  return {
    ...result,
    affiliationStatus: result.affiliation?.affiliationStatus || (isPending ? AffiliationStatus.PENDING : undefined),
    groupCode,
    errorCode: result.errorStatus !== ApplicationErrors.NO_ERROR ? result.errorStatus : undefined,
    hasError: result.errorStatus !== ApplicationErrors.NO_ERROR,
    isApproved: result.applicationStatus === ApplicationStatus.APPROVED,
    isPending,
    isSubmitted: isApplicationSubmitted(result.applicationStatus),
    request,
    team,
    teamId: team.teamId,
  };
}

/**
 * Application Slice
 */
const applicationSlice = createAppSlice({
  initialState: {
    teamClasses: null,
  },
  name: 'application',
  reducers: (create) => ({
    attemptGroupCode: create.asyncThunk(async (groupCode, { dispatch, rejectWithValue }) => {
      // First check if the group code exists and is redeemable
      const { payload } = await dispatch(applicationSlice.actions.checkGroupCode(groupCode));
      if (payload.errorCode) {
        return payload;
      }

      // Email is required in order to join the group or the application has questions,
      // or we're a fast pass so we want invalid passes to do something other than
      // just show invalidCodePage
      const { applicationForm: form, team, teamCode } = payload;
      const isFastPass = isFastPassCode(payload);
      if (
        form?.emailRequired ||
        form?.allowQuestionSkipWithEmail ||
        (isFastPass && (!teamCode.started || teamCode.ended || !teamCode.remainingRedemptionCount)) ||
        form?.questions?.length ||
        (team?.retailerTeam && team?.locationCount)
      ) {
        return payload;
      }

      // Submit the group code
      const response = await dispatch(applicationSlice.actions.submitGroupCode(groupCode));
      if (response.error) {
        throw rejectWithValue(response.error);
      }

      return {
        ...payload,
        ...response.payload,
      };
    }),

    checkGroupCode: create.asyncThunk(async (groupCode) => {
      try {
        const { applicationForm, team, ...teamCode } = await getTeamCode(groupCode);
        return {
          applicationForm: parseApplicationForm(applicationForm),
          groupCode,
          team: parseTeam(team),
          teamCode,
        };
      } catch (e) {
        return {
          errorCode: e.response.data?.type || 'NOT_FOUND',
          groupCode,
          status: e.response.status,
          statusText: e.response.statusText,
        };
      }
    }),

    submitGroupCode: create.asyncThunk(async (groupCode) => {
      const raw = await postTeamCode(groupCode);
      const { summary, teamCodeStatus } = raw;

      let errorCode =
        summary?.errorStatus === ApplicationErrors.NO_ERROR
          ? null
          : summary?.errorStatus || (teamCodeStatus !== 'INVALID' ? teamCodeStatus : null);
      if (teamCodeStatus === 'NO_LONGER_VALID') {
        errorCode = 'NO_LONGER_VALID';
      }
      const isPending = isApplicationPending(summary?.applicationStatus);
      return {
        ...raw,
        ...summary,
        affiliationStatus:
          summary.affiliation?.affiliationStatus || (isPending ? AffiliationStatus.PENDING : undefined),
        applicationForm: parseApplicationForm(raw.applicationForm),
        errorCode,
        groupCode,
        hasError: !!errorCode,
        isApproved: summary?.applicationStatus === ApplicationStatus.APPROVED,
        isPending,
        isSubmitted: isApplicationSubmitted(summary?.applicationStatus),
        teamId: raw.applicationForm?.teamId,
      };
    }, baseThunkConfig),

    createRetailLocation: create.asyncThunk((data) => postLocation(data), baseThunkConfig),

    fetchEmailRules: create.asyncThunk(({ email, formId }) => getEmailRules(formId, email), baseThunkConfig),

    fetchApplication: create.asyncThunk((appId) => getApplication(appId), baseThunkConfig),

    unsubmitApplication: create.asyncThunk((appId) => putUnsubmitApplication(appId), baseThunkConfig),

    resendApplicationVerificationEmail: create.asyncThunk(
      (appId) => postApplicationVerificationEmail(appId),
      baseThunkConfig,
    ),

    fetchLocations: create.asyncThunk((options = {}) => getLocations(options), baseThunkConfig),
    fetchPotentialTeams: create.asyncThunk(
      async (uuid, { dispatch }) => {
        const initialAffiliations = await getOnboardingAffiliations();
        const cached = readOnbCache(uuid);
        // A cache left behind by "Explore all teams" holds only landing identifiers, no team target
        const linkedTeams = hasLinkedTeams(cached) ? cached : undefined;
        if (linkedTeams || initialAffiliations.potentialAffiliations?.length > 0) {
          if (linkedTeams?.teamClass) {
            const teamClasses = await dispatch(applicationSlice.actions.fetchTeamClassPageInfo());
            const teamClassUrl = teamClasses.payload?.find(
              (t) => t.teamClass?.teamClassId === linkedTeams.teamClass,
            )?.pageUrl;
            if (teamClassUrl) {
              // Remove the localstorage so this only happens once, then redirect.
              localStorage.removeItem(getOnbCacheKey(uuid));
              window.location = teamClassUrl;
            }
            let teamIds = initialAffiliations.data?.potentialAffiliations.map((t) => t.teamId);
            if (linkedTeams?.teamId) {
              teamIds = teamIds.concat(linkedTeams?.teamId);
            }
            if (linkedTeams?.suggestedGroups) {
              teamIds = teamIds.concat(linkedTeams.suggestedGroups);
            }
            const teamSearchDto = {
              teamIds,
            };
            if (linkedTeams?.teamKey) {
              teamSearchDto.teamKeys = [linkedTeams.teamKey];
            }
            const response = await dispatch(applicationSlice.actions.fetchTeams(teamSearchDto));
            return response.payload?.content || [];
          }
          let teamIds = initialAffiliations.potentialAffiliations.map((t) => t.teamId);
          if (linkedTeams?.teamId) {
            teamIds = teamIds.concat(linkedTeams?.teamId);
          }
          if (linkedTeams?.suggestedGroups) {
            teamIds = teamIds.concat(linkedTeams.suggestedGroups);
          }
          const teamSearchDto = {
            teamIds,
          };
          if (linkedTeams?.teamKey) {
            teamSearchDto.teamKeys = [linkedTeams.teamKey];
          }
          const response = await dispatch(applicationSlice.actions.fetchTeams(teamSearchDto));
          return response.payload?.content || [];
        }
        return [];
      },
      {
        ...baseThunkConfig,
        fulfilled: (state, action) => {
          state.potentialAffiliations = action.payload;
        },
      },
    ),
    fetchPublicApplicationForm: create.asyncThunk(async (options = {}) => {
      const raw = await getPublicApplicationForm(options.teamId, options);
      return parseApplicationForm(raw);
    }, baseThunkConfig),

    fetchRenewalApplicationForm: create.asyncThunk(async (options = {}) => {
      const raw = await getRenewalApplicationForm(options.teamId, options);
      return parseApplicationForm(raw);
    }, baseThunkConfig),

    fetchTeamClasses: create.asyncThunk(() => getTeamClasses(), {
      ...baseThunkConfig,
      fulfilled: (state, action) => {
        state.teamClasses = action.payload;
      },
    }),
    fetchTeamClassPageInfo: create.asyncThunk(() => getTeamClassPageKeys(), {
      ...baseThunkConfig,
      fulfilled: (state, action) => {
        state.teamClasses = action.payload.map((tc) => ({
          ...tc.teamClass,
          pageKey: tc.pageKey,
          imageUrls: tc.imageUrls,
          pageUrl: tc.pageUrl,
        }));
      },
    }),

    fetchTeam: create.asyncThunk(async ({ teamId, teamKey }) => {
      if (!teamId && !teamKey) throw new Error('Either teamId or teamKey are required');
      return parseTeam(await getTeam({ teamId, teamKey }));
    }, baseThunkConfig),

    fetchTeams: create.asyncThunk(async (options, { signal }) => {
      const data = await getTeams({ ...(options ?? {}), signal });
      return {
        ...data,
        content: data.content.map((team) => parseTeam(team)),
      };
    }, baseThunkConfig),

    fetchVerificationStatuses: create.asyncThunk((ids) => postVerificationStatuses(ids), baseThunkConfig),

    geocodeAddress: create.asyncThunk((searchTerm) => getGeocode(searchTerm), baseThunkConfig),

    submitApplication: create.asyncThunk(async (data, { getState }) => {
      const context = selectContext(getState());
      const { team } = data;
      removeSuggestionFromStorage(context.user?.uuid, team?.teamId, team?.teamKey);
      // Clear the global alerts in case there's an affiliation one in there
      window.Exp?.globalAlerts?.clear?.('expiredAffiliations');
      return postApplicationRequest(data, context);
    }, baseThunkConfig),

    resubmitApplication: create.asyncThunk(async (data, { getState }) => {
      const context = selectContext(getState());
      const { teamApplicationId } = data;
      // Step 1: Revert the pending application to DRAFT so a new POST is accepted.
      await putUnsubmitApplication(teamApplicationId);
      // Step 2: Re-POST with the new file. Same endpoint as initial submit.
      return postApplicationRequest(data, context);
    }, baseThunkConfig),

    updateLocation: create.asyncThunk(async ({ affiliationId, locationId }) => {
      await putLocation(affiliationId, locationId);
      return { affiliationId, locationId };
    }, baseThunkConfig),
  }),
  selectors: {
    selectPotentialAffiliations: (state) => state.potentialAffiliations,
    selectTeamClasses: (state) => state.teamClasses,
  },
});

export const {
  attemptGroupCode,
  checkGroupCode,
  createRetailLocation,
  fetchApplication,
  fetchEmailRules,
  fetchLocations,
  fetchPotentialTeams,
  fetchPublicApplicationForm,
  fetchRenewalApplicationForm,
  fetchTeamClasses,
  fetchTeamClassPageInfo,
  fetchVerificationStatuses,
  fetchTeam,
  fetchTeams,
  geocodeAddress,
  resendApplicationVerificationEmail,
  resubmitApplication,
  submitApplication,
  submitGroupCode,
  unsubmitApplication,
  updateLocation,
} = applicationSlice.actions;

export const { selectTeamClasses, selectPotentialAffiliations } = applicationSlice.selectors;

export default applicationSlice;
